// Felder der Pillen-Formulare (Handoff §4): Beschriftung steht im Feld oben, aktiv gold, Fehler rot.
import { useState, type ChangeEvent, type FocusEvent, type HTMLAttributes, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { Info, type Icon } from '@phosphor-icons/react';
import { useViewport } from '../hooks';
import { FELD, FORM } from './form-stil';

/** Unter dieser Viewport-Breite stehen Felder einer Zeile untereinander (Prototyp `narrow`). */
const SCHMAL_UNTER = 600;

export interface FeldHuelleProps {
  label?: string;
  pflicht?: boolean;
  fehler?: boolean;
  tip?: string;
  /** Kleine Hinweiszeile unter dem Inhalt (Text aus dem Code). */
  hinweis?: ReactNode;
  /** Klickfläche ist ein <label> (Textfelder) statt eines <div>. */
  alsLabel?: boolean;
  children: ReactNode;
}

/** Gemeinsame Hülle für alle Feldarten: Fläche, Ring und Beschriftung je Zustand. */
export function FeldHuelle({ label, pflicht, fehler, tip, hinweis, alsLabel, children }: FeldHuelleProps) {
  const [fokus, setFokus] = useState(false);
  const z = fehler ? FELD.fehler : fokus ? FELD.fokus : FELD.ruhe;
  const raus = (e: FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFokus(false);
  };
  const props: HTMLAttributes<HTMLElement> & { 'data-tip'?: string } = {
    'data-tip': tip,
    onFocus: () => setFokus(true),
    onBlur: raus,
    className: 'pf-feld flex min-w-0 flex-col',
    style: {
      padding: '9px 13px 10px', borderRadius: FELD.radius, gap: 4, background: z.flaeche, boxShadow: z.ring,
      transform: fokus && !fehler ? 'translateY(-1px)' : 'none',
      transition: 'background .25s, box-shadow .3s, transform .35s cubic-bezier(.2,.9,.25,1.15)',
    },
  };
  const inhalt = (
    <>
      {label && (
        <span className="flex items-center" style={{ gap: 4, fontSize: 11, fontWeight: 700, color: z.label, transition: 'color .2s' }}>
          <span>{label}</span>
          {pflicht && <span aria-hidden style={{ color: FORM.rot }}>*</span>}
          {fehler && <span className="pf-drop" style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, color: FORM.rot }}>Pflichtfeld</span>}
        </span>
      )}
      {children}
      {hinweis && (
        <span className="flex items-center" style={{ gap: 5, fontSize: 11.5, fontWeight: 600, color: FORM.text3 }}>
          <Info aria-hidden size={12} weight="fill" className="shrink-0" />{hinweis}
        </span>
      )}
    </>
  );
  return alsLabel ? <label {...props}>{inhalt}</label> : <div {...props}>{inhalt}</div>;
}

type FeldTyp = 'text' | 'number' | 'date' | 'time' | 'datetime-local' | 'email' | 'tel' | 'url' | 'password';

export interface FormFeldProps {
  label: string;
  value: string | number;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  name?: string;
  typ?: FeldTyp;
  placeholder?: string;
  pflicht?: boolean;
  fehler?: boolean;
  tip?: string;
  hinweis?: ReactNode;
  /** Einheit rechts im Feld (z. B. „€“, „Min.“) – Text aus dem Code. */
  suffix?: string;
  icon?: Icon;
  /** Mehrzeilig (Textarea) mit dieser Zeilenzahl. */
  zeilen?: number;
  /** Zusätzliche Elemente rechts im Feld (Haken, Entfernen-Knopf). */
  rechts?: ReactNode;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  /** HTML-required (Browser-Prüfung), nur wo der Code es so hatte */
  required?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
  inputRef?: Ref<HTMLInputElement>;
  ariaAktiv?: string;
  ariaSteuert?: string;
}

export function FormFeld({
  label, value, onChange, name, typ = 'text', placeholder, pflicht, fehler, tip, hinweis, suffix, icon: I, zeilen, rechts,
  onFocus, onBlur, onKeyDown, autoFocus, disabled, required, readOnly, maxLength, min, max, step, inputMode, autoComplete, inputRef, ariaAktiv, ariaSteuert,
}: FormFeldProps) {
  const gemeinsam = {
    name, value, onChange, placeholder, autoFocus, disabled, required, readOnly, maxLength, onFocus, onBlur,
    'aria-invalid': fehler || undefined,
    'aria-required': pflicht || undefined,
  };
  return (
    <FeldHuelle label={label} pflicht={pflicht} fehler={fehler} tip={tip} hinweis={hinweis} alsLabel>
      {zeilen ? (
        <textarea {...gemeinsam} rows={zeilen} className="w-full resize-none border-none bg-transparent outline-none"
          style={{ font: 'inherit', fontSize: 15, fontWeight: 500, lineHeight: 1.45, color: FORM.text, padding: '2px 0' }} />
      ) : (
        <span className="flex min-w-0 items-center" style={{ gap: 8, minHeight: 24 }}>
          {I && <I aria-hidden size={14} weight="bold" className="shrink-0" color={fehler ? FELD.fehler.icon : FELD.ruhe.icon} />}
          <input {...gemeinsam} ref={inputRef} type={typ} min={min} max={max} step={step} inputMode={inputMode} autoComplete={autoComplete}
            onKeyDown={onKeyDown} aria-activedescendant={ariaAktiv} aria-controls={ariaSteuert}
            className="min-w-0 flex-1 border-none bg-transparent outline-none"
            style={{
              font: 'inherit', fontSize: 15, fontWeight: 600, color: FORM.text, padding: '2px 0', colorScheme: 'dark',
              fontVariantNumeric: 'tabular-nums', textAlign: suffix ? 'right' : 'left',
            }} />
          {suffix && <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>{suffix}</span>}
          {rechts}
        </span>
      )}
    </FeldHuelle>
  );
}

/** Eine Zeile mit 1–3 Feldern nebeneinander; schmal (< 600 px) untereinander. */
export function FormZeile({ spalten, children }: { spalten?: string; children: ReactNode }) {
  const { width } = useViewport();
  const anzahl = Array.isArray(children) ? children.filter(Boolean).length : 1;
  const schmal = width < SCHMAL_UNTER;
  const cols = schmal || anzahl < 2 ? 'minmax(0,1fr)' : spalten ?? Array.from({ length: anzahl }, () => 'minmax(0,1fr)').join(' ');
  return <div className="grid" style={{ gridTemplateColumns: cols, gap: 8 }}>{children}</div>;
}

/** Kleines Eingabefeld in einer Zeile (z. B. Objektnummer je Datei, Namen in einer Tabelle). */
export function FormKleinFeld({ value, onChange, placeholder, ariaLabel, tip, breite }: {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  ariaLabel: string;
  tip?: string;
  /** feste Breite in px; ohne Angabe füllt das Feld seine Zelle */
  breite?: number;
}) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder} aria-label={ariaLabel} data-tip={tip}
      className="pf-klein min-w-0 border-none outline-none"
      style={{
        width: breite ?? '100%', flexShrink: breite ? 0 : 1, height: 32, padding: '0 10px', borderRadius: 10, font: 'inherit',
        fontSize: 12.5, fontWeight: 700, color: FORM.text, background: 'rgba(255,255,255,0.08)',
        boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.12)', colorScheme: 'dark',
      }} />
  );
}
